import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { cloneDeep } from 'lodash';

import { ProjectWorksheetConfig } from '../constants';
import { useProjectContext } from '~/hooks';

interface ProjectWorksheetContextParams {
  config: typeof ProjectWorksheetConfig
  isSectionCollapsed: (id: string) => boolean
  toggleSectionCollapsed: (id: string) => void
  expandSection: (id: string) => void
  expandAllSections: () => void
  collapseAllSections: () => void
  isTocExpanded: (id: string) => boolean
  toggleTocExpanded: (id: string) => void
  actionBarHeight: number
  setActionBarHeight: (height: number) => void
}

const ProjectWorksheetContext = createContext({} as ProjectWorksheetContextParams);

interface ProjectWorksheetProviderProps {
  children?: ReactNode
}

const ProjectWorksheetProvider = ({ children }: ProjectWorksheetProviderProps) => {
  const {
    project,
    conditions: {
      isSectionDisplaying,
      isSubsectionDisplaying
    }
  } = useProjectContext();

  const config = useMemo(() => {
    const sections = cloneDeep(ProjectWorksheetConfig).filter(section => isSectionDisplaying(section.id));
    sections.forEach(section => {
      section.subsections = section.subsections.filter(subsection => isSubsectionDisplaying(subsection.id));
    });
    return sections;
  }, [isSectionDisplaying, isSubsectionDisplaying]);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const isSectionCollapsed = useCallback(
    (id: string) => !expandedSections[id],
    [expandedSections]
  );
  const toggleSectionCollapsed = useCallback((id: string) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);
  const expandSection = useCallback((id: string) => {
    setExpandedSections(prev => (prev[id] ? prev : { ...prev, [id]: true }));
  }, []);

  const [tocExpanded, setTocExpanded] = useState<Record<string, boolean>>({});
  const isTocExpanded = useCallback((id: string) => !!tocExpanded[id], [tocExpanded]);
  const toggleTocExpanded = useCallback((id: string) => {
    setTocExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const expandAllSections = useCallback(() => {
    const allExpanded = Object.fromEntries(config.map(section => [section.id, true]));
    setExpandedSections(allExpanded);
    setTocExpanded(allExpanded);
  }, [config]);
  const collapseAllSections = useCallback(() => {
    setExpandedSections({});
    setTocExpanded({});
  }, []);

  const [actionBarHeight, setActionBarHeight] = useState(0);

  ReactGA.send({
    hitType: 'pageview',
    page: '/project/id/worksheet',
    title: `${project?.name} | Conductor Solar`
  });
  document.title = `${project?.name} | Conductor Solar`;

  return (
    <ProjectWorksheetContext.Provider value={{ config, isSectionCollapsed, toggleSectionCollapsed, expandSection, expandAllSections, collapseAllSections, isTocExpanded, toggleTocExpanded, actionBarHeight, setActionBarHeight }}>
      {children}
    </ProjectWorksheetContext.Provider>
  );
};

export default ProjectWorksheetProvider;

export const useProjectWorksheetContext = () => useContext(ProjectWorksheetContext);
